import { Box, Paper, styled, Tooltip } from '@material-ui/core';
import { Collection, Model } from '@type-r/models';
import { useLinked } from '@type-r/react';
import * as React from 'react';
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { colors } from '../common';
import { Flex } from '../flex';
import { RoundCheckbox } from '../forms';
import InfiniteLoader from 'react-window-infinite-loader';

export interface TilesListProps {
    items : object[]
    itemSize : number
    renderer : React.ComponentType<ListChildComponentProps>
    selected? : Collection<Model>
    noItems? : any
    expandedAppBar? : boolean
    height? : number,
    maxVisibleItemCount?: number,
    selectedItem? : Model,
    isItemLoaded?: (index: number) => boolean,
    loadMoreItems?: (startIndex: number, stopIndex: number) => Promise<any>,
    itemCount?: number,
}

/**
 * Fixed size list which renders only visible elements
 * !!! Make sure to apply passed in *style* parameter !!!
 */
export const TilesList = ({ items, noItems, height, maxVisibleItemCount, itemSize, renderer, selected, expandedAppBar, selectedItem, isItemLoaded, loadMoreItems, itemCount} : TilesListProps ) => {

    if (maxVisibleItemCount) {
        const visibleItemCount = Math.min(items.length, maxVisibleItemCount);
        const calcHeight = visibleItemCount * itemSize + 20; // size to fit + scrollbar
        height = height ? Math.min(height, calcHeight) : calcHeight;
    }

    const $scrollToItem = useLinked(true);

    return items.length ?
    <Flex stretch>
        <AutoSizer disableHeight={ Boolean(height) }>
            { ({ width, height: _height }) =>
                <InfiniteLoader
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    loadMoreItems={loadMoreItems}>
                    {({ onItemsRendered, ref }) => (
                    <FixedSizeList
                        style={{ overflowX: 'auto' }}
                        width={width}
                        height={height || _height}
                        itemCount={ itemCount ? itemCount : items.length }
                        itemSize={ itemSize }
                        itemData={{ items, selected }}
                        ref={(ref) => {
                            if (ref && selectedItem && $scrollToItem.value) {
                                ref.scrollToItem(items.findIndex(item => item === selectedItem), 'smart');

                                $scrollToItem.false();
                            }
                        }}
                        onItemsRendered={onItemsRendered}
                        
                    >
                        { renderer }
                    </FixedSizeList>
                )}
                </InfiniteLoader>
            }
        </AutoSizer>
    </Flex>
    :
    <LeftTitle style={{ paddingLeft: '20px'}}>
        { noItems || 'No items to show' }
    </LeftTitle>
}

export const Tile = ({ $checked = undefined, large = false, style, onClick = undefined, color, left = undefined, title, aboveTitle = undefined, children, extra = undefined, leftTitle = undefined, right = undefined, icon = undefined }) =>
    <TilesRow
        onClick={ onClick }
        style={ style }
    >
        { $checked && <RoundCheckbox $checked={ $checked } marginRight={-30}/> }
        <PrimaryTile large={large}>
            <Flex alignCenter>
                {icon ? icon : <Box minWidth={50}/>} 
                <ColorBar color={ color } hasCheckbox={ Boolean( $checked ) }/>
            </Flex>            
            { left &&
                <Box minWidth={120} mr={1}>
                    <LeftTitle>
                        { leftTitle }
                    </LeftTitle>
                    <TileField>
                        { left }
                    </TileField>
                </Box>
            }

            <Flex column>
                {
                    aboveTitle ?
                        <Flex row>
                            { aboveTitle }
                        </Flex>
                    : undefined
                }
                <Title>
                    { title }
                </Title>
                <Flex row>
                    { children }
                </Flex>
            </Flex>
            { extra ?
                <Flex row grow>
                    { extra }
                </Flex>
            :null }
        </PrimaryTile>

        { right &&
            <SecondaryTile>
                { right }
            </SecondaryTile>
        }
    </TilesRow>

export const SmallTile = styled(
    ({
      color,
      menu,
      isSelected,
      $selected,
      className,
      footer,
      children,
      onClick,
      action,
      right
    }) => (
      <Paper className={className} onClick={onClick || $selected.true }>
        <div className="color-bar" />
        <div className="body">
          <div className="header">{children}</div>
          <div className="footer">{footer}</div>
        </div>
        { right ?
            <div className="small-tile-right">
                { right }
            </div>
        : null }
        <Flex center>{action}</Flex>
      </Paper>
    )
  )({
    cursor: "pointer",
    display: "flex",
    flexDirection: "row",
    height: 56,
    borderRadius: 2,
    borderStyle: "solid",
    borderWidth: 1,
    marginBottom: 8,
    borderColor: (p: any) => ( ( p.$selected ? p.$selected.value : p.isSelected  ) ? colors["yellow"] : "white"),
    backgroundColor: (p: any) => ( ( p.$selected ? p.$selected.value : p.isSelected  ) ? "#FFF8E5" : "white"),
  
    "& .small-tile-right" : {
        alignSelf: 'center',
        padding: 8,
        minWidth: '3em',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'row'
    },

    "& .body": {
      display: "flex",
      flexDirection: "column",
      padding: 8,
      width: "100%"
    },
  
    "& .header": {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      fontSize: 14,
      alignItems: "center",
      fontWeight: 600
    },
  
    "& .footer": {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      alignItems: "center",
      opacity: 0.75,
      fontSize: 12
    },
  
    "& .color-bar": {
      height: "100%",
      width: 4,
      backgroundColor: (p: any) => colors[p.color]
    }
  });
  
export const PrimaryTile = styled(
    ({ large, className, children }) =>
        <Paper className={ className + ( large ? ' large' : '' )}>
            { children }
        </Paper>
)({
    display: 'flex',
    flexGrow: 1,
    borderRadius: 0,
    flexDirection: 'row',
    padding: 8,

    '&.large' : {
        minWidth: 1100
    }
});

export const Title = styled( 'div' )({
    color: '#000',
    fontFamily: 'Montserrat',
    fontSize: 16,
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap'
})

export const LeftTitle = styled( 'div' )({
    marginTop: 4,
    marginBottom: 6,
    opacity: 0.75,
    whiteSpace: "nowrap",
    color: '#101010',
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '14px',
})

/**
 * Tile field label
 */
export const TileLabel = styled( 'span' )({
    color: '#728A97',
    marginRight: '1em',
    fontFamily: 'Montserrat',
    fontSize: 10,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
})

export const TileField = styled(
    ({ label, children, className, long, compact }) =>
        <div className={ className + ( compact ? ' compact' : '' )}>
            { label &&
                <TileLabel>
                    { label }
                </TileLabel>
            }
            { long ?
                <Tooltip title={ children }>
                    <span className="value long">
                        { children }
                    </span>
                </Tooltip>
            :
                <span className="value short">
                    { children }
                </span>
            }
        </div>
)({
    '&.compact' : {
        flexDirection: 'column',
        minWidth: 100,
        justifyContent: 'center',
    },

    minWidth: 140,

    display: 'flex',
    fontFamily: 'Montserrat',
    fontSize: 10,
    marginRight: '1em',

    '& .value' : {
        marginRight: '1em',
        color: 'black',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },

    '& .short' : {
        maxWidth: 95,
    },

    '& .long' : {
        maxWidth: 140,
    }
})

export const SecondaryTile = styled(
    ({ className, children }) =>
        <Paper square className={ className }>
            { children }
        </Paper>
)({
    backgroundColor: '#FBFCFD',
    alignItems: 'center',
    display: 'flex',
    zIndex : -1
})

export const TilesRow = styled( 'div' )({
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    padding: 2
})

export const ColorBar = ({ color, hasCheckbox = undefined }) =>
    <Box
        alignSelf="stretch"
        width={2}
        mr={1}
        ml={ hasCheckbox ? 1 : 0 }
        bgcolor={ color }
    />
